import useSyncStore from '../stores/sync';
import { useNotificationsStore } from '../stores/notifications';
import { toast } from 'sonner';
import { useSessionStore } from '@/stores/session';
import peerManager from './peer-manager';

/**
 * Manages a singleton WebSocket connection for the application's notifications and status updates.
 */
class NotificationsWebSocketManager {
  private static instance: NotificationsWebSocketManager;
  private ws: WebSocket | null = null;
  private reconnectAttempts = 0;
  private isConnectedViaWebRTC = false;

  private constructor() {}

  public static getInstance(): NotificationsWebSocketManager {
    if (!NotificationsWebSocketManager.instance) {
      NotificationsWebSocketManager.instance = new NotificationsWebSocketManager();
    }
    return NotificationsWebSocketManager.instance;
  }

  public connect(): void {
    const { communicationMode } = useSessionStore.getState();
    if (communicationMode === 'webrtc') {
      this.connectViaWebRTC();
      return;
    }

    if (this.ws && (this.ws.readyState === WebSocket.OPEN || this.ws.readyState === WebSocket.CONNECTING)) {
      return;
    }
    if (this.ws) {
      this.disconnect();
    }
    
    const token = localStorage.getItem('mobile_auth_token');
    const protocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
    let url = `${protocol}//${window.location.host}/api/ws/notifications`;
    if (token) {
      url += `?token=${encodeURIComponent(token)}`;
    }

    console.log('Connecting to Notifications WebSocket...');
    this.ws = new WebSocket(url);

    this.ws.onopen = () => {
      console.log('Notifications WebSocket connection established.');
      this.reconnectAttempts = 0;
    };

    this.ws.onmessage = (event) => {
      try {
        const message = JSON.parse(event.data);
        const { type, payload } = message;
        
        switch (type) {
          case 'patches':
            if (payload) useSyncStore.getState().applyServerPatches(payload.newRev, payload.patches);
            break;
          case 'new_notification':
            useNotificationsStore.getState().fetchAll(); // Refetch to get new data and update count
            if (payload) {
              toast.info(payload.title, {
                description: payload.body,
                action: payload.action ? {
                  label: 'View',
                  onClick: () => window.dispatchEvent(new CustomEvent('navigate-to', { detail: payload.action.targetPage }))
                } : undefined,
              });
            }
            break;
          case 'notifications_updated':
            useNotificationsStore.getState().fetchAll();
            break;
          default:
            console.log('Received unhandled notification WebSocket message:', message);
        }
      } catch (error) {
        console.error('Failed to parse notification WebSocket message:', error);
      }
    };

    this.ws.onclose = (event) => {
      if (event.code !== 1000) { 
        console.log('Notifications WebSocket closed. Attempting to reconnect...');
        this.reconnect();
      }
    };

    this.ws.onerror = (error) => {
      console.error('Notifications WebSocket error:', error);
      this.ws?.close();
    };
  }
  
  public disconnect(): void {
    if (this.ws) {
        this.ws.onclose = () => {};
        this.ws.close(1000, "User disconnected");
        this.ws = null;
        console.log('Notifications WebSocket disconnected.');
    }
  }

  private reconnect(): void {
    const delay = Math.min(1000 * (2 ** this.reconnectAttempts), 30000);
    setTimeout(() => {
      this.reconnectAttempts++;
      this.connect();
    }, delay);
  }

  // WebRTC logic would be similar to the other manager if needed
  private connectViaWebRTC(): void {
    // Implementation for WebRTC proxying would go here
  }
}

const notificationsManager = NotificationsWebSocketManager.getInstance();
export default notificationsManager;